'use client'

// Technology dossier — long-form scientific page.
// Hero → Section A (Synthetic Biology) → Section B (Impact Areas) →
// Section C (Safety / Ethics / Regulation). Editorial restraint, no
// marketing language. All copy from messages.tech.{...}.

import { useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'
import PlasmidSVG from './tech/PlasmidSVG'
import DNAHelix from './tech/DNAHelix'
import SpecimenFrame from './tech/SpecimenFrame'
import ChapterIndex from './tech/ChapterIndex'
import { ShieldIcon, InstitutionIcon, LeafIcon } from './tech/PillarIcons'
import ProcessTimeline from './tech/ProcessTimeline'
import HeroHelix from './tech/HeroHelix'
import TypewriterText from './tech/TypewriterText'
import SafetyAccordion, { ApprovedSeal } from './tech/SafetyAccordion'

// ─── Tokens ──────────────────────────────────────────────────────────────
const CREAM       = '#F5EED9'
const CREAM_WARM  = '#EDE2C0'
const CREAM_DEEP  = '#D6C9A0'
const EARTH       = '#513205'
const EARTH_70    = 'rgba(81,50,5,0.70)'
const EARTH_50    = 'rgba(81,50,5,0.50)'
const EARTH_30    = 'rgba(81,50,5,0.30)'
const EARTH_15    = 'rgba(81,50,5,0.15)'
const OLIVE       = '#5B7005'
const OLIVE_60    = 'rgba(91,112,5,0.60)'
const LIME        = '#9FAF08'
const FONT_BODY   = 'var(--font-body)'
const FONT_DISP   = 'var(--font-display)'
const FONT_SERIF  = 'var(--font-serif)'

// Asset paths (URL-encoded) — also used as the fallback when the CMS is
// unreachable. The four keys are load-bearing — the markup below references
// LAB.plasmid / LAB.gel1 / LAB.gel2 / LAB.proteinGel directly.
const LAB_FALLBACK = {
  plasmid:    '/TECH/Lab/Captura%20de%20pantalla%202025-03-28%20103210.png',
  gel1:       '/TECH/Lab/Captura%20de%20pantalla%202025-04-01%20115158.png',
  gel2:       '/TECH/Lab/Captura%20de%20pantalla%202025-06-29%20133645.png',
  proteinGel: '/TECH/Lab/Captura%20de%20pantalla%202026-04-14%20121744.png.2131.png',
}
export type LabSpecimensMap = typeof LAB_FALLBACK
const LOGOS = {
  fpdgi1:    '/TECH/FPdGi-Logo%201.jpg',
  fpdgi2:    '/TECH/FPdGi-Logo%202.png',
  fpdgi3:    '/TECH/FPdGi-Logo%203.jpg',
  fpdgi4:    '/TECH/FPdGi-Logo%204.png',
  startupv:  '/TECH/Startupv-Logo.png',
  euGreen:   '/TECH/Pacto%20Verde%20Europeo.jpg',
}

// ─── Reusable bits ───────────────────────────────────────────────────────
function CornerBracket({ corner, color = EARTH_30 }: { corner: 'tl'|'tr'|'bl'|'br'; color?: string }) {
  const d = {
    tl: 'M0 10 L0 0 L10 0', tr: 'M8 0 L18 0 L18 10',
    bl: 'M0 8 L0 18 L10 18', br: 'M8 18 L18 18 L18 8',
  }[corner]
  const pos = {
    tl: { top: 16, left: 16 }, tr: { top: 16, right: 16 },
    bl: { bottom: 16, left: 16 }, br: { bottom: 16, right: 16 },
  }[corner]
  return (
    <svg aria-hidden="true" viewBox="0 0 18 18"
      style={{ position:'absolute', width:18, height:18, ...pos, pointerEvents:'none', zIndex:5 }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1} />
    </svg>
  )
}

// ─── Section A header ────────────────────────────────────────────────────
interface SectionHeaderProps {
  letter: 'A' | 'B' | 'C'
  chrome: string
  title: string
  subtitle: string
}
function SectionHeader({ letter, chrome, title, subtitle }: SectionHeaderProps) {
  const letterRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLParagraphElement>(null)
  const chromeRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (reduced) return
    gsapSetup()
    const trigger = ScrollTrigger.create({
      trigger: letterRef.current,
      start: 'top 75%',
      once: true,
      onEnter: () => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
        if (letterRef.current) {
          tl.fromTo(letterRef.current,
            { autoAlpha: 0, scale: 0.6, rotation: -5 },
            { autoAlpha: 1, scale: 1, rotation: 0, duration: 1.2, ease: 'power3.out' })
        }
        if (titleRef.current) {
          tl.fromTo(titleRef.current, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.6 }, '-=0.6')
        }
        if (subtitleRef.current) {
          tl.fromTo(subtitleRef.current, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5 }, '-=0.3')
        }
        if (chromeRef.current) {
          tl.fromTo(chromeRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4 }, '-=0.2')
        }
      },
    })
    return () => trigger.kill()
  }, [reduced])

  return (
    <div style={{ position: 'relative', padding: 'clamp(80px, 12vh, 160px) 0', minHeight: '50vh' }}>
      {/* The massive letter */}
      <div
        ref={letterRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 'clamp(-20px, -2vw, 0px)',
          fontFamily: FONT_SERIF,
          fontStyle: 'italic',
          fontSize: 'clamp(15rem, 26vw, 32rem)',
          lineHeight: 0.85,
          letterSpacing: '-0.05em',
          color: OLIVE,
          opacity: reduced ? 0.18 : 0.16,
          fontWeight: 400,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 1,
          willChange: 'transform, opacity',
        }}
      >
        {letter}
      </div>
      <div style={{ position: 'relative', zIndex: 2, paddingLeft: 'clamp(120px, 18vw, 280px)' }}>
        <div
          ref={chromeRef}
          style={{
            fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.38em',
            textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
            marginBottom: 24,
          }}
        >
          {chrome}
        </div>
        <h2
          ref={titleRef}
          style={{
            fontFamily: FONT_DISP,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 500,
            letterSpacing: '-0.025em',
            lineHeight: 1.0,
            color: EARTH,
            margin: 0,
          }}
        >
          {title}
        </h2>
        <p
          ref={subtitleRef}
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
            color: OLIVE,
            margin: '14px 0 0',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            fontWeight: 400,
          }}
        >
          {subtitle}
        </p>
      </div>
    </div>
  )
}

// ─── Key point block ─────────────────────────────────────────────────────
interface KeyPointProps {
  number: string
  title: string
  body: string
  visual: React.ReactNode    // any visual element (plasmid, specimen, etc.)
  reverse?: boolean
}
function KeyPoint({ number, title, body, visual, reverse }: KeyPointProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (reduced || !wrapRef.current) return
    gsapSetup()
    const items = wrapRef.current.querySelectorAll<HTMLElement>('[data-anim]')
    gsap.set(items, { autoAlpha: 0, y: 24 })
    const trigger = ScrollTrigger.create({
      trigger: wrapRef.current,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        gsap.to(items, {
          autoAlpha: 1, y: 0,
          duration: 0.7, ease: 'power3.out', stagger: 0.12,
        })
      },
    })
    return () => trigger.kill()
  }, [reduced])

  return (
    <div
      ref={wrapRef}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'clamp(40px, 6vw, 96px)',
        alignItems: 'center',
        margin: 'clamp(72px, 10vh, 128px) 0',
        direction: reverse ? 'rtl' : 'ltr',
      }}
      className="tech-keypoint"
    >
      {/* Text — direction reset so content reads LTR even when grid is reversed */}
      <div style={{ direction: 'ltr' }}>
        <div
          data-anim
          style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          }}
        >
          <span style={{
            fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400,
            fontSize: 'clamp(1.5rem, 2.4vw, 2rem)', color: LIME,
            lineHeight: 1, letterSpacing: '-0.02em',
          }}>
            {number}
          </span>
          <span aria-hidden="true" style={{
            flex: 1, height: 1, background: EARTH_15,
          }} />
        </div>
        <h3
          data-anim
          style={{
            fontFamily: FONT_DISP, fontWeight: 500,
            fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
            lineHeight: 1.1, letterSpacing: '-0.02em',
            color: EARTH, margin: '0 0 20px',
          }}
        >
          {title}
        </h3>
        <p
          data-anim
          style={{
            fontFamily: FONT_BODY, fontSize: 'clamp(15px, 1.15vw, 18px)',
            lineHeight: 1.7, color: EARTH_70, margin: 0, maxWidth: '38ch',
          }}
        >
          {body}
        </p>
      </div>
      {/* Visual — any node (plasmid SVG, specimen frame, etc.) */}
      <div data-anim style={{ direction: 'ltr', display: 'flex', justifyContent: 'center' }}>
        {visual}
      </div>
    </div>
  )
}

// ─── Animated metric ─────────────────────────────────────────────────────
interface MetricProps {
  sign: string
  value: string
  suffix: string
  label: string
}
function Metric({ sign, value, suffix, label }: MetricProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const numRef = useRef<HTMLSpanElement>(null)
  const reduced = useReducedMotion()
  const numericValue = parseInt(value, 10)

  useEffect(() => {
    if (!wrapRef.current || !numRef.current) return
    if (reduced) {
      numRef.current.textContent = value
      return
    }
    gsapSetup()
    const target = numRef.current
    target.textContent = '0'
    const trigger = ScrollTrigger.create({
      trigger: wrapRef.current,
      start: 'top 75%',
      once: true,
      onEnter: () => {
        const obj = { v: 0 }
        gsap.to(obj, {
          v: numericValue,
          duration: 1.5,
          ease: 'power4.out',
          onUpdate: () => {
            target.textContent = String(Math.round(obj.v))
          },
        })
      },
    })
    return () => trigger.kill()
  }, [numericValue, value, reduced])

  return (
    <div ref={wrapRef} style={{ textAlign: 'center', padding: 'clamp(40px, 6vh, 80px) 0' }}>
      <div
        style={{
          fontFamily: FONT_DISP,
          fontSize: 'clamp(7rem, 16vw, 14rem)',
          fontWeight: 600,
          lineHeight: 0.9,
          letterSpacing: '-0.045em',
          color: LIME,
          textShadow: `0 0 24px rgba(159,175,8,0.18)`,
          margin: 0,
        }}
      >
        <span>{sign}</span>
        <span ref={numRef} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <span>{suffix}</span>
      </div>
      <div
        style={{
          fontFamily: FONT_BODY,
          fontSize: 11,
          letterSpacing: '0.36em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: OLIVE,
          marginTop: 20,
        }}
      >
        {label}
      </div>
    </div>
  )
}

// ─── Pillar (Section B / C) ──────────────────────────────────────────────
interface PillarProps {
  label: string
  title: string
  body: string
  outcome?: string
  metric?: { sign: string; value: string; suffix: string; label: string }
  icon?: React.ReactNode
  children?: React.ReactNode
}
function Pillar({ label, title, body, outcome, metric, icon, children }: PillarProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (reduced || !wrapRef.current) return
    gsapSetup()
    const items = wrapRef.current.querySelectorAll<HTMLElement>('[data-pillar-anim]')
    gsap.set(items, { autoAlpha: 0, y: 24 })
    const trigger = ScrollTrigger.create({
      trigger: wrapRef.current,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        gsap.to(items, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 })
      },
    })
    return () => trigger.kill()
  }, [reduced])

  return (
    <div
      ref={wrapRef}
      style={{
        padding: 'clamp(48px, 8vh, 96px) 0',
        borderTop: `1px solid ${EARTH_15}`,
        position: 'relative',
      }}
    >
      {icon && (
        <div data-pillar-anim style={{ marginBottom: 22 }}>
          {icon}
        </div>
      )}
      <div
        data-pillar-anim
        style={{
          fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.36em',
          textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
          marginBottom: 18,
        }}
      >
        {label}
      </div>
      <h3
        data-pillar-anim
        style={{
          fontFamily: FONT_DISP, fontWeight: 500,
          fontSize: 'clamp(2rem, 3.6vw, 2.75rem)',
          lineHeight: 1.05, letterSpacing: '-0.025em',
          color: EARTH, margin: '0 0 24px', maxWidth: '20ch',
        }}
      >
        {title}
      </h3>
      <p
        data-pillar-anim
        style={{
          fontFamily: FONT_BODY, fontSize: 'clamp(15px, 1.15vw, 18px)',
          lineHeight: 1.7, color: EARTH_70, margin: 0, maxWidth: '60ch',
        }}
      >
        {body}
      </p>
      {metric && (
        <div data-pillar-anim>
          <Metric sign={metric.sign} value={metric.value} suffix={metric.suffix} label={metric.label} />
        </div>
      )}
      {outcome && (
        <p
          data-pillar-anim
          style={{
            fontFamily: FONT_SERIF, fontStyle: 'italic',
            fontSize: 'clamp(1rem, 1.4vw, 1.2rem)',
            color: EARTH_70, lineHeight: 1.5, margin: 0, maxWidth: '50ch',
          }}
        >
          {outcome}
        </p>
      )}
      {children && <div data-pillar-anim style={{ marginTop: 32 }}>{children}</div>}
    </div>
  )
}

// ─── Comparative bars ────────────────────────────────────────────────────
interface BarsProps {
  header: string
  bar1: { label: string; value: string; pct: number }
  bar2: { label: string; value: string; pct: number }
  source: string
}
function ComparativeBars({ header, bar1, bar2, source }: BarsProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const bar1Ref = useRef<HTMLDivElement>(null)
  const bar2Ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (!bar1Ref.current || !bar2Ref.current) return
    if (reduced) {
      bar1Ref.current.style.width = `${bar1.pct}%`
      bar2Ref.current.style.width = `${bar2.pct}%`
      return
    }
    gsapSetup()
    gsap.set([bar1Ref.current, bar2Ref.current], { width: 0 })
    const trigger = ScrollTrigger.create({
      trigger: wrapRef.current,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        gsap.to(bar1Ref.current, { width: `${bar1.pct}%`, duration: 1.0, ease: 'power3.out' })
        gsap.to(bar2Ref.current, { width: `${bar2.pct}%`, duration: 1.2, ease: 'power3.out', delay: 0.15 })
      },
    })
    return () => trigger.kill()
  }, [bar1.pct, bar2.pct, reduced])

  const Row = ({ label, value, refEl, color }: { label: string; value: string; refEl: React.RefObject<HTMLDivElement | null>; color: string }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 8,
        fontFamily: FONT_BODY, fontSize: 13, letterSpacing: '0.04em',
        color: EARTH_70,
      }}>
        <span>{label}</span>
        <span style={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: EARTH,
        }}>
          {value}
        </span>
      </div>
      <div style={{
        height: 14,
        background: 'rgba(81,50,5,0.06)',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        <div ref={refEl} style={{ height: '100%', background: color, borderRadius: 2 }} />
      </div>
    </div>
  )

  return (
    <div ref={wrapRef} style={{ margin: 'clamp(48px, 8vh, 80px) 0', maxWidth: 720 }}>
      <div style={{
        fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.36em',
        textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
        marginBottom: 24,
      }}>
        {header}
      </div>
      <Row label={bar1.label} value={bar1.value} refEl={bar1Ref} color="rgba(123,90,42,0.55)" />
      <Row label={bar2.label} value={bar2.value} refEl={bar2Ref} color={LIME} />
      <p style={{
        fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.24em',
        textTransform: 'uppercase', color: EARTH_50,
        margin: '24px 0 0',
      }}>
        {source}
      </p>
    </div>
  )
}

// ─── Main page ───────────────────────────────────────────────────────────
export type LabStripItem = { src: string; caption: string; alt?: string }

export default function TechPageClient({
  lab,
  specimens,
}: {
  lab?: Partial<LabSpecimensMap>
  /** Full ordered list of CMS specimens for the auto-scrolling strip. */
  specimens?: LabStripItem[]
} = {}) {
  const LAB: LabSpecimensMap = { ...LAB_FALLBACK, ...(lab ?? {}) }
  const t = useTranslations('tech')
  const [labStripPaused, setLabStripPaused] = useState(false)
  const heroRef = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()

  // Hero entrance animation
  useEffect(() => {
    if (reduced || !heroRef.current) return
    gsapSetup()
    const items = heroRef.current.querySelectorAll<HTMLElement>('[data-hero-anim]')
    gsap.set(items, { autoAlpha: 0, y: 24 })
    gsap.to(items, {
      autoAlpha: 1, y: 0,
      duration: 0.7, ease: 'power3.out', stagger: 0.1, delay: 0.2,
    })
  }, [reduced])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  // Lab strip — prefer the CMS-supplied list (auto-reflects every edit:
  // images, captions, ordering, additions). Falls back to the bundled set
  // built from the keyed LAB shape when CMS is empty or unreachable.
  const labStripImages: LabStripItem[] = (specimens && specimens.length > 0)
    ? specimens
    : [
        { src: LAB.gel1,       caption: t('a02Caption') },
        { src: LAB.proteinGel, caption: t('a03Caption') },
        { src: LAB.gel2,       caption: t('a02Caption') },
        { src: LAB.plasmid,    caption: t('a01Caption') },
      ]

  return (
    <main
      style={{
        background: CREAM,
        color: EARTH,
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .tech-content { max-width: 1280px; margin: 0 auto; padding: 0 clamp(24px, 5vw, 80px); }
        .tech-prose { max-width: 720px; }
        .tech-image:hover { transform: scale(1.02); box-shadow: 0 12px 40px rgba(81,50,5,0.10); }

        .tech-bg-A { background: linear-gradient(180deg, ${CREAM} 0%, ${CREAM_WARM} 100%); }
        .tech-bg-B { background: linear-gradient(180deg, ${CREAM_WARM} 0%, ${CREAM_DEEP} 100%); }
        .tech-bg-C { background: linear-gradient(180deg, ${CREAM_DEEP} 0%, ${CREAM} 100%); }

        .tech-chapter-card {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 22px;
          border: 1px solid ${EARTH_15};
          border-radius: 8px;
          background: rgba(255,255,255,0.32);
          cursor: pointer;
          transition: transform 250ms ease, border-color 250ms ease, box-shadow 250ms ease;
          font-family: ${FONT_BODY};
          font-size: 14px;
          color: ${EARTH};
          text-decoration: none;
        }
        .tech-chapter-card:hover {
          transform: translateY(-3px);
          border-color: ${OLIVE_60};
          box-shadow: 0 8px 24px rgba(81,50,5,0.08);
        }
        .tech-chapter-card .ch-letter {
          font-family: ${FONT_SERIF}; font-style: italic; font-weight: 400;
          font-size: 28px; color: ${LIME}; line-height: 1; letter-spacing: -0.02em;
        }
        .tech-chapter-card .ch-label {
          font-weight: 500;
          letter-spacing: 0.02em;
        }

        @keyframes techStripScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .tech-strip-track {
          display: flex; gap: 24px;
          width: max-content;
          animation: techStripScroll 45s linear infinite;
          will-change: transform;
        }
        .tech-strip-track[data-paused="true"] { animation-play-state: paused; }

        .tech-tag {
          display: inline-block;
          padding: 6px 12px;
          border: 1px solid ${EARTH_15};
          border-radius: 99px;
          font-family: ${FONT_BODY};
          font-size: 10.5px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: ${EARTH_70};
          font-weight: 600;
          margin: 4px 6px 0 0;
        }

        .tech-icon {
          width: 36px; height: 36px;
          margin-bottom: 18px;
          color: ${OLIVE};
        }

        .tech-logo {
          display: flex; align-items: center; justify-content: center;
          padding: 18px 22px;
          border: 1px solid ${EARTH_15};
          border-radius: 6px;
          background: rgba(255,255,255,0.55);
          height: 100px;
          transition: filter 300ms ease, transform 300ms ease, opacity 300ms ease, border-color 300ms ease;
          filter: grayscale(100%) brightness(0.5);
          opacity: 0.7;
        }
        .tech-logo:hover {
          filter: none;
          opacity: 1;
          transform: scale(1.03);
          border-color: ${OLIVE_60};
        }
        .tech-logo-grid:hover .tech-logo:not(:hover) {
          opacity: 0.4;
        }
        .tech-logo-placeholder {
          display: flex; align-items: center; justify-content: center;
          padding: 18px 22px;
          border: 1px dashed ${EARTH_15};
          border-radius: 6px;
          background: rgba(255,255,255,0.22);
          height: 100px;
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.28em; text-transform: uppercase;
          color: ${EARTH_50};
          font-weight: 600;
          text-align: center;
        }

        .tech-helix-deco { display: none; }
        @media (min-width: 1280px) {
          .tech-helix-deco { display: block; }
        }

        /* Hero bento grid ─────────────────────────────────────── */
        .tech-hero-grid {
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
        }
        @media (max-width: 1023px) {
          .tech-hero-grid { grid-template-columns: 1fr; }
          .tech-hero-bento { display: none; }
        }

        .tech-hero-bento {
          display: flex; flex-direction: column;
          gap: clamp(16px, 1.6vw, 22px);
        }

        .tech-hero-card {
          position: relative;
          background: rgba(255,255,255,0.55);
          border: 1px solid ${EARTH_15};
          border-radius: 14px;
          padding: 16px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 0 12px 36px rgba(81,50,5,0.06);
          overflow: hidden;
        }
        .tech-hero-card-rain {
          height: clamp(280px, 36vh, 380px);
          display: flex; flex-direction: column;
        }
        .tech-hero-card-head {
          display: flex; align-items: center; gap: 10px;
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.28em; text-transform: uppercase;
          font-weight: 600; color: ${EARTH_70};
          padding-bottom: 12px;
          border-bottom: 1px solid ${EARTH_15};
        }
        .tech-hero-card-head .tech-hero-card-id {
          margin-left: auto;
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          letter-spacing: 0.12em;
          color: ${OLIVE};
        }
        .tech-hero-card-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: ${LIME};
          box-shadow: 0 0 8px ${LIME};
          animation: tech-hero-card-dot-pulse 1.6s ease-in-out infinite;
        }
        @keyframes tech-hero-card-dot-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.4); }
        }
        .tech-hero-card-body {
          flex: 1;
          margin: 12px -16px;
          position: relative;
        }
        .tech-hero-card-foot {
          display: flex; justify-content: space-between; align-items: center;
          padding-top: 12px;
          border-top: 1px solid ${EARTH_15};
          font-family: ${FONT_BODY}; font-size: 10.5px;
          letter-spacing: 0.18em; text-transform: uppercase;
          color: ${EARTH_50}; font-weight: 600;
        }
        .tech-hero-card-mono {
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          letter-spacing: 0.08em;
          color: ${LIME};
        }

        /* Mini stat row ───────────────────────────────────────── */
        .tech-hero-mini-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }
        .tech-hero-mini {
          background: rgba(255,255,255,0.42);
          border: 1px solid ${EARTH_15};
          border-radius: 12px;
          padding: 14px 14px 12px;
          transition: transform 240ms ease, border-color 240ms ease, background 240ms ease;
        }
        .tech-hero-mini:hover {
          transform: translateY(-2px);
          border-color: ${OLIVE_60};
          background: rgba(255,255,255,0.65);
        }
        .tech-hero-mini-num {
          font-family: ${FONT_DISP};
          font-size: clamp(1.8rem, 2.4vw, 2.4rem);
          font-weight: 600; color: ${EARTH};
          line-height: 1; letter-spacing: -0.03em;
          font-variant-numeric: tabular-nums;
        }
        .tech-hero-mini-lbl {
          font-family: ${FONT_BODY}; font-size: 10px;
          letter-spacing: 0.22em; text-transform: uppercase;
          color: ${OLIVE}; font-weight: 600;
          margin-top: 8px;
        }

        /* Marquee ─────────────────────────────────────────────── */
        .tech-hero-marquee {
          position: absolute;
          left: 0; right: 0;
          bottom: 0;
          padding: 14px 0;
          border-top: 1px solid ${EARTH_15};
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.35) 100%);
          overflow: hidden;
          z-index: 4;
          mask-image: linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%);
        }
        .tech-hero-marquee-track {
          display: flex;
          width: max-content;
          animation: tech-hero-marquee-kf 48s linear infinite;
        }
        @keyframes tech-hero-marquee-kf {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .tech-hero-marquee-row {
          display: inline-flex; align-items: center;
          font-family: ${FONT_BODY};
          font-size: 11.5px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          font-weight: 600;
          color: ${EARTH_70};
          white-space: nowrap;
        }
        .tech-hero-marquee-row > span { padding: 0 18px; }
        .tech-hero-marquee-row > span:nth-child(4n+1) { color: ${LIME}; }
        .tech-hero-marquee-row > i {
          font-style: normal; color: ${EARTH_30};
          font-size: 10px;
        }

        @media (prefers-reduced-motion: reduce) {
          .tech-hero-marquee-track,
          .tech-hero-card-dot { animation: none !important; }
        }

        /* Section C — alliance strip ─────────────────────────── */
        .tech-c-alliance {
          margin-top: clamp(56px, 9vh, 96px);
        }
        .tech-c-alliance-head {
          display: flex; align-items: center; gap: 14px;
          font-family: ${FONT_BODY}; font-size: 11px;
          letter-spacing: 0.42em; text-transform: uppercase;
          font-weight: 600; color: ${OLIVE};
          margin-bottom: 16px;
        }
        .tech-c-alliance-sub {
          font-family: ${FONT_SERIF}; font-style: italic;
          font-size: clamp(1.15rem, 1.7vw, 1.45rem);
          color: ${EARTH}; line-height: 1.4; font-weight: 400;
          margin: 0 0 36px; max-width: 50ch;
        }
        .tech-c-alliance-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 14px;
        }
        @keyframes tech-c-alliance-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .tech-c-alliance-tile {
          position: relative;
          height: 110px;
          display: flex; align-items: center; justify-content: center;
          padding: 18px 22px;
          background: rgba(255,255,255,0.42);
          border: 1px solid ${EARTH_15};
          border-radius: 12px;
          opacity: 0;
          animation: tech-c-alliance-in 700ms cubic-bezier(0.22,1,0.36,1) forwards;
          transition: transform 320ms cubic-bezier(0.22,1,0.36,1),
                      border-color 320ms ease,
                      background 320ms ease,
                      box-shadow 320ms ease;
        }
        .tech-c-alliance-tile img {
          filter: grayscale(100%) brightness(0.55);
          opacity: 0.78;
          transition: filter 320ms ease, opacity 320ms ease, transform 320ms ease;
        }
        .tech-c-alliance-tile:hover {
          transform: translateY(-3px);
          border-color: ${OLIVE_60};
          background: rgba(255,255,255,0.7);
          box-shadow: 0 14px 32px rgba(81,50,5,0.10);
        }
        .tech-c-alliance-tile:hover img {
          filter: none;
          opacity: 1;
          transform: scale(1.04);
        }
        .tech-c-alliance-stamp {
          position: absolute;
          top: 10px; right: 10px;
          width: 22px; height: 22px;
          border-radius: 50%;
          background: ${LIME};
          color: ${EARTH};
          display: flex; align-items: center; justify-content: center;
          opacity: 0;
          transform: scale(0.6) rotate(-25deg);
          transition: opacity 280ms ease, transform 360ms cubic-bezier(0.22,1,0.36,1);
          box-shadow: 0 0 0 4px rgba(159,175,8,0.18);
        }
        .tech-c-alliance-tile:hover .tech-c-alliance-stamp {
          opacity: 1;
          transform: scale(1) rotate(0deg);
        }

        @media (prefers-reduced-motion: reduce) {
          .tech-c-alliance-tile { animation: none !important; opacity: 1 !important; }
        }

        @media (max-width: 1023px) {
          .tech-keypoint { grid-template-columns: 1fr !important; direction: ltr !important; gap: 32px !important; }
        }
        @media (max-width: 767px) {
          .tech-strip-track { animation-duration: 28s; }
        }

        @media (prefers-reduced-motion: reduce) {
          .tech-strip-track { animation: none !important; }
        }
      `}</style>

      {/* Fixed right-rail chapter index (desktop only) */}
      <ChapterIndex
        chapters={[
          { id: 'tech-section-a', letter: 'A', label: t('chapterAShort') },
          { id: 'tech-section-b', letter: 'B', label: t('chapterBShort') },
          { id: 'tech-section-c', letter: 'C', label: t('chapterCShort') },
        ]}
      />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        id="tech-hero"
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: CREAM,
          paddingTop: 'clamp(110px, 14vh, 160px)',
          paddingBottom: 'clamp(110px, 14vh, 160px)',
        }}
      >
        {/* Soft cream→warm gradient wash for depth */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse 80% 60% at 70% 40%, rgba(159,175,8,0.08) 0%, transparent 60%),
                       radial-gradient(ellipse 60% 50% at 20% 80%, rgba(81,50,5,0.06) 0%, transparent 60%)`,
          pointerEvents: 'none',
          zIndex: 1,
        }} />

        {/* Grain */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/grain.svg)', backgroundSize: '220px 220px',
          opacity: 0.05, mixBlendMode: 'multiply', pointerEvents: 'none',
          zIndex: 3,
        }} />

        {/* Top chrome — mission-briefing typewriter */}
        <div className="tech-content" style={{
          position: 'relative',
          zIndex: 4,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontFamily: FONT_BODY, fontSize: 10.5, letterSpacing: '0.36em',
          textTransform: 'uppercase', fontWeight: 600, color: EARTH_50,
          marginBottom: 'clamp(60px, 9vh, 100px)',
        }}>
          <TypewriterText
            text={t('chromeTopLeft')}
            delaySec={0.35}
            holdSec={5}
            caretColor={LIME}
          />
          <TypewriterText
            text={t('chromeTopRight')}
            delaySec={0.6}
            holdSec={5}
            caretColor={LIME}
          />
        </div>

        {/* Hero bento grid */}
        <div className="tech-content tech-hero-grid" style={{
          flex: 1, position: 'relative', zIndex: 4,
          display: 'grid',
          gap: 'clamp(32px, 4vw, 64px)',
          alignItems: 'center',
        }}>
          {/* Left — typography */}
          <div className="tech-hero-text">
            <p
              data-hero-anim
              style={{
                fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.42em',
                textTransform: 'uppercase', fontWeight: 600, color: OLIVE,
                margin: '0 0 28px',
              }}
            >
              {t('heroEyebrow')}
            </p>
            <h1
              data-hero-anim
              style={{
                fontFamily: FONT_DISP, fontWeight: 500,
                fontSize: 'clamp(3rem, 7.4vw, 6.5rem)',
                lineHeight: 0.95, letterSpacing: '-0.035em',
                color: EARTH, margin: 0, maxWidth: '13ch',
              }}
            >
              <span style={{ display: 'block' }}>{t('heroHeadlineLine1')}</span>
              <span style={{ display: 'block' }}>
                <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', color: OLIVE, fontWeight: 400 }}>
                  {t('heroHeadlineLine2')}
                </span>
              </span>
            </h1>
            <p
              data-hero-anim
              style={{
                fontFamily: FONT_BODY, fontSize: 'clamp(15px, 1.2vw, 19px)',
                lineHeight: 1.65, color: EARTH_70,
                margin: '28px 0 36px', maxWidth: 540,
              }}
            >
              {t('heroSubhead')}
            </p>

            {/* Chapter indicator cards */}
            <div
              data-hero-anim
              style={{
                display: 'flex', flexWrap: 'wrap', gap: 12,
              }}
            >
              <button onClick={() => scrollTo('tech-section-a')} className="tech-chapter-card" type="button">
                <span className="ch-letter">A</span>
                <span className="ch-label">{t('chapterAJump')}</span>
              </button>
              <button onClick={() => scrollTo('tech-section-b')} className="tech-chapter-card" type="button">
                <span className="ch-letter">B</span>
                <span className="ch-label">{t('chapterBJump')}</span>
              </button>
              <button onClick={() => scrollTo('tech-section-c')} className="tech-chapter-card" type="button">
                <span className="ch-letter">C</span>
                <span className="ch-label">{t('chapterCJump')}</span>
              </button>
            </div>
          </div>

          {/* Right — bento card stack */}
          <div className="tech-hero-bento" data-hero-anim>
            {/* Big card: live DNA code rain */}
            <div className="tech-hero-card tech-hero-card-rain">
              <div className="tech-hero-card-head">
                <span className="tech-hero-card-dot" aria-hidden="true" />
                <span>Live · Sequencing</span>
                <span className="tech-hero-card-id">SP-25-A02</span>
              </div>
              <div className="tech-hero-card-body">
                <HeroHelix />
              </div>
              <div className="tech-hero-card-foot">
                <span>M. meliloti / pBSL-21</span>
                <span className="tech-hero-card-mono">+12.4 kb</span>
              </div>
            </div>

            {/* Mini stat cards row */}
            <div className="tech-hero-mini-row">
              <div className="tech-hero-mini">
                <div className="tech-hero-mini-num">12</div>
                <div className="tech-hero-mini-lbl">Strains modified</div>
              </div>
              <div className="tech-hero-mini">
                <div className="tech-hero-mini-num">84</div>
                <div className="tech-hero-mini-lbl">Generations tested</div>
              </div>
              <div className="tech-hero-mini">
                <div className="tech-hero-mini-num">2.4<span style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>×</span></div>
                <div className="tech-hero-mini-lbl">Soil carbon ↑</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip — biotech keywords scrolling along the bottom */}
        <div data-hero-anim className="tech-hero-marquee" aria-hidden="true">
          <div className="tech-hero-marquee-track">
            {Array.from({ length: 2 }).map((_, copy) => (
              <span key={copy} className="tech-hero-marquee-row">
                <span>M. meliloti</span><i>·</i>
                <span>pBSL-21</span><i>·</i>
                <span>CRISPR-Cas9</span><i>·</i>
                <span>Nitrogen fixation</span><i>·</i>
                <span>Glomus mosseae</span><i>·</i>
                <span>Rhizosphere</span><i>·</i>
                <span>Soil carbon sequestration</span><i>·</i>
                <span>ATGCATGCATGC</span><i>·</i>
                <span>Synthetic biology</span><i>·</i>
                <span>Biosafety BSL-1</span><i>·</i>
                <span>UPV Valencia</span><i>·</i>
                <span>Volume II — 2026</span><i>·</i>
              </span>
            ))}
          </div>
        </div>

        {/* Scroll cue */}
        <div
          data-hero-anim
          aria-hidden="true"
          style={{
            position: 'absolute', bottom: 'clamp(56px, 7vh, 88px)',
            left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            zIndex: 4,
          }}
        >
          <span style={{
            fontFamily: FONT_BODY, fontSize: 10, letterSpacing: '0.36em',
            textTransform: 'uppercase', fontWeight: 600, color: EARTH_50,
          }}>{t('scrollBegin')}</span>
          <span style={{
            display: 'block', width: 1, height: 28,
            background: `linear-gradient(to bottom, ${EARTH_30} 0%, transparent 100%)`,
          }} />
        </div>

        <CornerBracket corner="tl" />
        <CornerBracket corner="tr" />
      </section>

      {/* ── SECTION A: SYNTHETIC BIOLOGY ───────────────────────────── */}
      <section
        id="tech-section-a"
        className="tech-bg-A"
        style={{ position: 'relative', paddingBottom: 'clamp(80px, 12vh, 140px)' }}
      >
        {/* DNA helix decoration — desktop margin only, not over content */}
        <div aria-hidden="true" className="tech-helix-deco" style={{
          position: 'absolute', left: 'clamp(8px, 1.5vw, 24px)',
          top: '20%', width: 80, height: 480, opacity: 0.45,
          pointerEvents: 'none', zIndex: 1,
        }}>
          <DNAHelix height={480} />
        </div>

        <div className="tech-content" style={{ position: 'relative', zIndex: 2 }}>
          <SectionHeader
            letter="A"
            chrome={t('sectionAChrome')}
            title={t('sectionATitle')}
            subtitle={t('sectionASubtitle')}
          />

          {/* Concept statement */}
          <div
            style={{
              maxWidth: 720, margin: '0 auto',
              padding: 'clamp(40px, 6vh, 64px) 0',
              borderTop: `1px solid ${EARTH_15}`,
              borderBottom: `1px solid ${EARTH_15}`,
              textAlign: 'center',
            }}
          >
            <p style={{
              fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400,
              fontSize: 'clamp(1.4rem, 2.4vw, 1.85rem)',
              lineHeight: 1.4, color: EARTH,
              margin: '0 0 24px', letterSpacing: '-0.005em',
            }}>
              <span style={{ color: LIME, opacity: 0.8 }}>“</span>
              {t('sectionAQuote')}
              <span style={{ color: LIME, opacity: 0.8 }}>”</span>
            </p>
            <p style={{
              fontFamily: FONT_BODY,
              fontSize: 'clamp(15px, 1.15vw, 18px)',
              lineHeight: 1.7, color: EARTH_70, margin: 0,
            }}>
              {t('sectionAConcept')}
            </p>
          </div>

          <KeyPoint
            number={t('a01Number')}
            title={t('a01Title')}
            body={t('a01Body')}
            visual={
              <div style={{ width: '100%', maxWidth: 480 }}>
                <PlasmidSVG />
                <div style={{
                  fontFamily: FONT_BODY, fontSize: 12, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: EARTH_70, fontWeight: 500,
                  marginTop: 14, lineHeight: 1.5, textAlign: 'center',
                }}>
                  <span style={{
                    color: LIME, fontFamily: FONT_SERIF,
                    fontStyle: 'italic', fontSize: 14, letterSpacing: '-0.01em',
                    fontWeight: 400, marginRight: 8,
                  }}>
                    fig.
                  </span>
                  {t('a01Caption')}
                </div>
              </div>
            }
          />
          <KeyPoint
            number={t('a02Number')}
            title={t('a02Title')}
            body={t('a02Body')}
            visual={
              <SpecimenFrame
                src={LAB.gel1}
                alt={t('a02Caption')}
                caption={t('a02Caption')}
                specimenId="SP-25-A02 / GEL-04"
              />
            }
            reverse
          />
          <KeyPoint
            number={t('a03Number')}
            title={t('a03Title')}
            body={t('a03Body')}
            visual={
              <SpecimenFrame
                src={LAB.proteinGel}
                alt={t('a03Caption')}
                caption={t('a03Caption')}
                specimenId="SP-25-A03 / PRO-12"
              />
            }
          />
        </div>

        {/* Lab strip — full width, auto-scrolling */}
        <div
          style={{
            marginTop: 'clamp(48px, 8vh, 96px)',
            paddingBottom: 'clamp(40px, 6vh, 64px)',
          }}
          onMouseEnter={() => setLabStripPaused(true)}
          onMouseLeave={() => setLabStripPaused(false)}
        >
          <div className="tech-content" style={{
            fontFamily: FONT_BODY, fontSize: 10.5, letterSpacing: '0.36em',
            textTransform: 'uppercase', fontWeight: 600, color: EARTH_50,
            marginBottom: 24,
          }}>
            ── {t('labStripLabel')}
          </div>
          <div style={{ overflow: 'hidden', position: 'relative' }}>
            <div className="tech-strip-track" data-paused={labStripPaused || undefined}>
              {[...labStripImages, ...labStripImages].map((img, i) => (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    width: 360, height: 240,
                    flexShrink: 0,
                    border: `1px solid ${EARTH_15}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.4)',
                  }}
                >
                  <Image
                    src={img.src}
                    alt={img.caption}
                    fill
                    sizes="360px"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION B: IMPACT AREAS ──────────────────────────────── */}
      <section
        id="tech-section-b"
        className="tech-bg-B"
        style={{ position: 'relative', paddingBottom: 'clamp(80px, 12vh, 140px)' }}
      >
        <div className="tech-content">
          <SectionHeader
            letter="B"
            chrome={t('sectionBChrome')}
            title={t('sectionBTitle')}
            subtitle={t('sectionBSubtitle')}
          />

          <Pillar
            label={t('b01Label')}
            title={t('b01Title')}
            body={t('b01Body')}
            metric={{
              sign:   t('b01MetricSign'),
              value:  t('b01Metric'),
              suffix: t('b01MetricSuffix'),
              label:  t('b01MetricLabel'),
            }}
            outcome={t('b01Outcome')}
          />

          {/* Experimental timeline — gives the metrics temporal context */}
          <ProcessTimeline
            title={t('timelineTitle')}
            milestones={[
              { week: 0,  label: t('timelineW0') },
              { week: 3,  label: t('timelineW3') },
              { week: 6,  label: t('timelineW6') },
              { week: 10, label: t('timelineW10') },
            ]}
          />

          <Pillar
            label={t('b02Label')}
            title={t('b02Title')}
            body={t('b02Body')}
            metric={{
              sign:   t('b02MetricSign'),
              value:  t('b02Metric'),
              suffix: t('b02MetricSuffix'),
              label:  t('b02MetricLabel'),
            }}
            outcome={t('b02Outcome')}
          />

          <ComparativeBars
            header={t('comparisonHeader')}
            bar1={{ label: t('comparisonBar1Label'), value: t('comparisonBar1Value'), pct: 100 }}
            bar2={{ label: t('comparisonBar2Label'), value: t('comparisonBar2Value'), pct: 80 }}
            source={t('comparisonSource')}
          />
        </div>
      </section>

      {/* ── SECTION C: SAFETY, ETHICS, REGULATION ─────────────────── */}
      <section
        id="tech-section-c"
        className="tech-bg-C"
        style={{ position: 'relative', paddingBottom: 'clamp(120px, 16vh, 200px)' }}
      >
        <div className="tech-content">
          <SectionHeader
            letter="C"
            chrome={t('sectionCChrome')}
            title={t('sectionCTitle')}
            subtitle={t('sectionCSubtitle')}
          />

          <SafetyAccordion
            pillars={[
              {
                label: 'C.01',
                title: t('c01Title'),
                body:  t('c01Body'),
                icon:  <ShieldIcon size={44} />,
                bullets: [t('c01Tag1'), t('c01Tag2'), t('c01Tag3')],
                metric: '100%',
                metricLabel: t('c01MeterLabel'),
              },
              {
                label: 'C.02',
                title: t('c02Title'),
                body:  t('c02Body'),
                icon:  <InstitutionIcon size={44} />,
                bullets: [
                  t('logoFpdgiAlt'),
                  t('logoStartupvAlt'),
                  'ISO 9001:2015',
                ],
                metric: '5+',
                metricLabel: t('c02MeterLabel'),
              },
              {
                label: 'C.03',
                title: t('c03Title'),
                body:  t('c03Body'),
                icon:  <LeafIcon size={44} />,
                bullets: [
                  t('logoEuGreenAlt'),
                  'UN SDG 2 · 13 · 15',
                  'Net-positive carbon pathway',
                ],
                metric: '100%',
                metricLabel: t('c03MeterLabel'),
              },
            ]}
          />

          {/* Alliance strip — interactive partner grid with hover stamp */}
          <div className="tech-c-alliance">
            <div className="tech-c-alliance-head">
              <span aria-hidden="true">──</span>
              <span>{t('credibilityHeader')}</span>
            </div>
            <p className="tech-c-alliance-sub">{t('credibilitySub')}</p>
            <div className="tech-c-alliance-grid">
              {[
                { src: LOGOS.fpdgi1,  alt: t('logoFpdgiAlt') },
                { src: LOGOS.fpdgi2,  alt: t('logoFpdgiAlt') },
                { src: LOGOS.fpdgi4,  alt: t('logoFpdgiAlt') },
                { src: LOGOS.startupv,alt: t('logoStartupvAlt') },
                { src: LOGOS.euGreen, alt: t('logoEuGreenAlt') },
              ].map((logo, i) => (
                <div key={i} className="tech-c-alliance-tile" style={{ animationDelay: `${i * 90}ms` }}>
                  <Image
                    src={logo.src} alt={logo.alt}
                    width={140} height={70}
                    style={{ objectFit: 'contain', maxHeight: 60, width: 'auto' }}
                  />
                  <span className="tech-c-alliance-stamp" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Final approved seal */}
          <ApprovedSeal
            ringText={t('sealRingText')}
            centerLabel={t('sealCenterLabel')}
            yearLabel="UPV · 2026"
          />

          {/* Footer note */}
          <div style={{
            marginTop: 'clamp(80px, 14vh, 160px)',
            paddingTop: 32,
            borderTop: `1px solid ${EARTH_15}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 16,
            fontFamily: FONT_BODY, fontSize: 10.5, letterSpacing: '0.36em',
            textTransform: 'uppercase', fontWeight: 600, color: EARTH_50,
          }}>
            <span>{t('footerNote')}</span>
            <span>BIOSOIL · UPV · 2026</span>
          </div>
        </div>
      </section>
    </main>
  )
}
